import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ReviewSubmissionForm,
  API,
  type ReviewTenantBranding,
} from "@etlyn/etlyn-cms";

interface ReviewCompany {
  name?: string;
  website?: string;
  logo_url?: string;
  metadata?: Record<string, unknown>;
}

const getStringValue = (value: unknown) => {
  return typeof value === "string" && value.trim() && value.trim() !== "string"
    ? value.trim()
    : undefined;
};

const getNestedStringValue = (source: unknown, path: string[]) => {
  let current = source;
  for (const key of path) {
    if (!current || typeof current !== "object" || !(key in current)) {
      return undefined;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return getStringValue(current);
};

const getBrandColor = (
  metadata: ReviewCompany["metadata"],
  paths: string[][]
) => {
  for (const path of paths) {
    const value = getNestedStringValue(metadata, path);
    if (value && /^#(?:[0-9a-fA-F]{3}){1,2}$/.test(value)) {
      return value;
    }
  }
  return undefined;
};

const buildTenantBranding = (company?: ReviewCompany): ReviewTenantBranding => {
  const metadata = company?.metadata;
  return {
    companyName: getStringValue(company?.name),
    logoUrl:
      getStringValue(company?.logo_url) ??
      getNestedStringValue(metadata, ["branding", "logoUrl"]) ??
      getNestedStringValue(metadata, ["branding", "logo_url"]),
    website: getStringValue(company?.website),
    primaryColor: getBrandColor(metadata, [
      ["reviews", "primaryColor"],
      ["reviews", "primary_color"],
      ["branding", "primaryColor"],
      ["branding", "primary_color"],
      ["theme", "primaryColor"],
      ["theme", "primary_color"],
      ["primaryColor"],
      ["primary_color"],
    ]),
    accentColor: getBrandColor(metadata, [
      ["reviews", "accentColor"],
      ["reviews", "accent_color"],
      ["branding", "accentColor"],
      ["branding", "accent_color"],
      ["theme", "accentColor"],
      ["theme", "accent_color"],
      ["accentColor"],
      ["accent_color"],
    ]),
  };
};

export function ReviewSubmission() {
  const { token } = useParams<{ token: string }>();
  const [tenantBranding, setTenantBranding] = useState<ReviewTenantBranding>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      try {
        if (!token) {
          setError("No review token provided");
          setLoading(false);
          return;
        }

        // Validate token with backend and get company info
        const response = await API.get(`/validate-review-token/${token}`);

        if (response.data.valid) {
          setTenantBranding(buildTenantBranding(response.data.company));
        } else {
          setError("Invalid or expired review link");
        }

        setLoading(false);
      } catch (err: any) {
        console.error("Token validation error:", err);
        const errorMessage =
          err.response?.data?.detail || "Invalid or expired review link";
        setError(errorMessage);
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  if (loading || error) {
    return (
      <main
        className="ecms-root ui-toolkit ecms-auth-page"
        data-etlyn-appearance="rebrand"
        data-etlyn-color-mode="light"
      >
        <section className="ecms-auth-card" role={error ? "alert" : "status"}>
          <h1>{error ? "Invalid review link" : "Loading review form"}</h1>
          <p>{error || "Please wait while we check this review link."}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white px-5 py-12">
      <div className="mx-auto w-full max-w-2xl">
        <ReviewSubmissionForm
          appearance="rebrand"
          token={token!}
          tenantBranding={tenantBranding}
          onSuccess={() => {
            console.log("Review submitted successfully!");
          }}
          onError={(error) => {
            console.error("Review submission error:", error);
          }}
        />
      </div>
    </main>
  );
}
